import React from 'react';

import { Avatar } from '@poseidon/components/Avatar';
import { ButtonA } from '@poseidon/components/Input2';
import { Arrows, Communication, General, Time } from '@poseidon/components/StrokeIcons';
import { AnimatePresence, motion } from 'framer-motion';

import { CDNImage } from '~/components/CDNImage';
import { LogoIcon, LogoText } from '~/components/ToneIcons';

function forceStringToUrl(str: string) {
  return str.startsWith('http://') || str.startsWith('https://') ? str : 'https://' + str;
}

type LocalDocumentPublic = {
  primaryColor: string;
  secondaryColor: string;
  fileContentType: string;
  fileUrl: string;
};

type LocalUserTeamPublic = {
  name: string | null;
  email: string | null;
  title: string | null;
  teamLogo: string | null;
  teamName: string;
  calendarLink: string | null;
  calendarText: string | null;
  contactNumber: string | null;
  profilePicture: string | null;
};

interface DocumentAsideProps {
  data: { sharing: LocalDocumentPublic; userTeam: LocalUserTeamPublic };
}

export const DocumentAside = ({ data: { sharing: document, userTeam } }: DocumentAsideProps) => {
  const [mobileExpanded, setMobileExpanded] = React.useState(false);
  const displayName = userTeam.name ?? userTeam.email ?? '';

  return (
    <div
      className="relative flex w-full shrink-0 justify-stretch text-white backdrop-blur-xl sm:h-full sm:w-[26%] sm:flex-col sm:border-l sm:border-white/10"
      style={{ backgroundColor: document.primaryColor + 'cc' }}>
      {/* Accent gradient bar */}
      <div
        className="absolute left-0 right-0 top-0 h-8 sm:h-10"
        style={{
          background: `linear-gradient(to right, ${document.secondaryColor}, ${document.primaryColor})`,
        }}
      />

      {/* ── Desktop sidebar ── */}
      <div className="relative hidden w-full grow flex-col sm:flex">
        <div className="flex grow flex-col items-center pt-14 md:pt-24">
          <ProfilePhoto userTeam={userTeam} secondaryColor={document.secondaryColor} />
          <div className="mt-4 flex flex-col items-center gap-1 px-4 text-center">
            <div className="text-lg font-semibold tracking-tight sm:text-3xl">{displayName}</div>
            {userTeam.title && (
              <div className="flex items-center gap-2 text-white/60">
                {userTeam.teamLogo && (
                  <Avatar srcs={[userTeam.teamLogo]} className="size-4" shape="circle" alt={userTeam.teamName} />
                )}
                <span className="text-sm">{userTeam.title}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 px-4 pb-4">
          {userTeam.calendarLink && (
            <ButtonA
              href={forceStringToUrl(userTeam.calendarLink)}
              target="_blank"
              rel="noopener noreferrer"
              color="blue"
              icon={<Time.CalendarDate />}
              className="w-full justify-center">
              {userTeam.calendarText || 'Schedule a Call'}
            </ButtonA>
          )}

          {userTeam.email && (
            <ActionRow
              icon={<Communication.Mail02 />}
              label="Email"
              href={'mailto:' + userTeam.email}
              value={userTeam.email}
            />
          )}
          {userTeam.contactNumber && (
            <ActionRow
              icon={<Communication.Phone />}
              label="Phone"
              href={'tel:' + userTeam.contactNumber}
              value={userTeam.contactNumber}
            />
          )}
          {document.fileContentType === 'application/pdf' && (
            <ActionRow
              icon={<General.Download01 />}
              label="Download"
              href={document.fileUrl}
              value="Full PDF"
            />
          )}
        </div>

        <div className="flex w-full items-center justify-center border-t border-white/10 p-2.5">
          <a
            rel="noreferrer"
            target="_blank"
            href="https://useposeidon.com"
            className="inline-flex items-center gap-1.5 text-2xs text-white/30 no-underline transition-all hover:text-white/50">
            Powered by
            <LogoIcon className="size-4" />
            <LogoText className="h-3 w-auto" />
          </a>
        </div>
      </div>

      {/* ── Mobile header ── */}
      <div className="relative flex w-full flex-col sm:hidden">
        {/* Compact bar */}
        <button
          onClick={() => setMobileExpanded((v) => !v)}
          className="flex w-full items-center gap-3 px-3 pb-2 pt-10">
          <ProfilePhotoSmall userTeam={userTeam} />
          <div className="flex min-w-0 flex-1 flex-col items-start">
            <div className="truncate text-sm font-semibold">{displayName}</div>
            {userTeam.title && <div className="truncate text-xs text-white/60">{userTeam.title}</div>}
          </div>
          <motion.div animate={{ rotate: mobileExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <Arrows.ChevronDown className="size-5" />
          </motion.div>
        </button>

        {/* Collapsed: icon-only action row */}
        {!mobileExpanded && (
          <div className="flex items-center gap-2 px-3 pb-2">
            {userTeam.calendarLink && (
              <a
                href={forceStringToUrl(userTeam.calendarLink)}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20">
                <Time.CalendarDate className="size-4" />
              </a>
            )}
            {userTeam.email && (
              <a
                href={'mailto:' + userTeam.email}
                className="rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20">
                <Communication.Mail02 className="size-4" />
              </a>
            )}
            {userTeam.contactNumber && (
              <a
                href={'tel:' + userTeam.contactNumber}
                className="rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20">
                <Communication.Phone className="size-4" />
              </a>
            )}
          </div>
        )}

        {/* Expanded details */}
        <AnimatePresence>
          {mobileExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden">
              <div className="flex flex-col gap-2 px-3 pb-3">
                {userTeam.calendarLink && (
                  <ButtonA
                    href={forceStringToUrl(userTeam.calendarLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="blue"
                    size="sm"
                    icon={<Time.CalendarDate />}
                    className="w-full justify-center">
                    {userTeam.calendarText || 'Schedule a Call'}
                  </ButtonA>
                )}
                {userTeam.email && (
                  <ActionRow
                    icon={<Communication.Mail02 />}
                    label="Email"
                    href={'mailto:' + userTeam.email}
                    value={userTeam.email}
                  />
                )}
                {userTeam.contactNumber && (
                  <ActionRow
                    icon={<Communication.Phone />}
                    label="Phone"
                    href={'tel:' + userTeam.contactNumber}
                    value={userTeam.contactNumber}
                  />
                )}
                {document.fileContentType === 'application/pdf' && (
                  <ActionRow
                    icon={<General.Download01 />}
                    label="Download"
                    href={document.fileUrl}
                    value="Full PDF"
                  />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/* ── Profile photo (desktop) ── */
const ProfilePhoto = ({
  userTeam,
  secondaryColor,
}: {
  userTeam: LocalUserTeamPublic;
  secondaryColor: string;
}) => {
  const name = userTeam.name ?? userTeam.email ?? '';

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      whileHover={{ scale: 1.03 }}
      className="group relative">
      {/* Gradient ring glow */}
      <div
        className="absolute -inset-1 rounded-full opacity-0 blur-xs transition-opacity duration-500 group-hover:opacity-60 sm:-inset-1.5"
        style={{
          background: `linear-gradient(135deg, ${secondaryColor}, white, ${secondaryColor})`,
        }}
      />
      {userTeam.profilePicture ? (
        <div
          className="relative size-24 overflow-hidden rounded-full ring-2 ring-white/20 transition-all duration-500 group-hover:ring-white/50 sm:size-28 md:size-36"
          >
          <CDNImage width={150} height={150} src={userTeam.profilePicture} alt={name} className="size-full object-cover" />
        </div>
      ) : (
        <Avatar
          srcs={[]}
          alt={name}
          shape="circle"
          coloredBackground
          className="relative size-24 text-3xl ring-2 ring-white/20 transition-all duration-500 group-hover:ring-white/50 sm:size-28 md:size-36"
        />
      )}
    </motion.div>
  );
};

/* ── Profile photo (mobile compact) ── */
const ProfilePhotoSmall = ({ userTeam }: { userTeam: LocalUserTeamPublic }) => {
  const name = userTeam.name ?? userTeam.email ?? '';

  if (userTeam.profilePicture) {
    return (
      <div className="size-10 shrink-0 overflow-hidden rounded-full">
        <CDNImage width={40} height={40} src={userTeam.profilePicture} alt={name} className="size-full object-cover" />
      </div>
    );
  }

  return (
    <Avatar srcs={[]} alt={name} shape="circle" coloredBackground className="size-10 shrink-0" />
  );
};

/* ── Action row card ── */
type ActionRowProps = {
  icon: React.JSX.Element;
  label: string;
  value: string;
  href: string;
};

const ActionRow = ({ icon, label, value, href }: ActionRowProps) => (
  <a
    href={href}
    className="flex items-center gap-3 rounded-lg bg-white/[0.07] px-3 py-2.5 no-underline transition-colors hover:bg-white/[0.14]">
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-white [&>svg]:size-4">
      {icon}
    </div>
    <div className="flex min-w-0 flex-col">
      <span className="text-2xs text-white/50">{label}</span>
      <span className="truncate text-sm text-white">{value}</span>
    </div>
  </a>
);
